import React from 'react';

const ROW_HEIGHT = 32;

/** Widths for skeleton cells to create a natural, varied appearance */
const SKELETON_WIDTHS = [60, 80, 45, 90, 70, 55, 85, 65, 75, 50];

interface SkeletonRowProps {
  rowIndex: number;
  columnCount: number;
}

/** A single placeholder row with animated skeleton cells, used while buffer data is loading */
export const SkeletonRow = React.memo(function SkeletonRow({ rowIndex, columnCount }: SkeletonRowProps) {
  return (
    <tr
      className={`border-b border-border/50 ${rowIndex % 2 === 0 ? 'bg-background' : 'bg-muted/20'}`}
      style={{ height: ROW_HEIGHT }}
      aria-hidden="true"
      data-testid={`skeleton-cell-placeholder-${rowIndex}`}
    >
      {Array.from({ length: columnCount }, (_, colIdx) => (
        <td
          key={colIdx}
          className="px-3 py-1 border-r border-grid-rule last:border-r-0"
        >
          <div
            className="h-3.5 rounded bg-muted/60 animate-pulse"
            style={{
              width: `${SKELETON_WIDTHS[(rowIndex + colIdx) % SKELETON_WIDTHS.length]}%`,
              animationDelay: `${((rowIndex + colIdx) % 5) * 100}ms`,
            }}
          />
        </td>
      ))}
      {/* PP-05 filler — carries the row hairline to the right edge.
          This component is rendered by BOTH grids (`ResultsGrid` and
          `TableExplorerGrid` both import `SkeletonRows`), so it is the one
          filler site where the two-grid pair shares a single implementation —
          and it was the last of the nine to miss the sweep.

          `select-none` is measured, not cosmetic: without it Chromium
          serialises the empty cell as a trailing tab, so drag-selecting a row
          and copying yielded `1\tAlice\t` instead of `1\tAlice`. `width: 100%`
          makes the column greedy under `table-layout: auto` so the slack lands
          here rather than stretching the real columns — matching the live
          header/body fillers in both grids. */}
      <td aria-hidden="true" data-grid-filler="true" className="p-0 border-0 select-none" style={{ width: '100%' }} />
    </tr>
  );
});

interface SkeletonRowsProps {
  startIndex: number;
  count: number;
  columnCount: number;
}

/** Renders a batch of skeleton rows for a given range */
export function SkeletonRows({ startIndex, count, columnCount }: SkeletonRowsProps) {
  if (count <= 0 || columnCount <= 0) return null;
  return (
    <>
      {Array.from({ length: count }, (_, i) => (
        <SkeletonRow
          key={`skel-${startIndex + i}`}
          rowIndex={startIndex + i}
          columnCount={columnCount}
        />
      ))}
    </>
  );
}
